import { useState, useEffect, useCallback, useRef, createContext, useContext, type ReactNode } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../services/api'
import { useTelegram } from './useTelegram'
import { useCurator } from '../contexts/CuratorContext'

// Access gate: the API rejects Mini App logins for Telegram users who aren't
// provisioned clients (403 ACCESS_CLOSED). The bot's persistent menu button
// opens the app for everyone, so we mirror the bot's gate by closing the app
// for non-clients instead of rendering the UI.
function isAccessClosed(error: unknown): boolean {
  const resp = (error as { response?: { status?: number; data?: { error?: string } } })?.response
  return resp?.status === 403 && resp?.data?.error === 'ACCESS_CLOSED'
}

function closeMiniApp() {
  try {
    window.Telegram?.WebApp?.close?.()
  } catch { /* not inside Telegram (e.g. browser preview) — nothing to close */ }
}

// True only inside a real Telegram client. `platform` is 'unknown' in a plain
// browser (telegram-web-app.js still loads there). Used to ensure the dev-mock
// auto-login NEVER runs inside Telegram — otherwise a real user whose initData
// is transiently empty would be logged in as the NBL_Dev test persona.
function isRealTelegram(): boolean {
  const p = window.Telegram?.WebApp?.platform
  return !!p && p !== 'unknown'
}

/**
 * Состояние авторизации. Экземпляр ДОЛЖЕН быть один на приложение — отсюда
 * AuthProvider ниже.
 *
 * Пока хук вызывался напрямую из 27 компонентов, каждый смонтированный
 * потребитель заводил своё состояние и свой авто-логин: на прод-стенде это
 * давало 5-6 одновременных POST /auth/telegram на один холодный старт
 * (65 672 логина за 48 ч против 2477 запросов /auth/me, которые react-query
 * дедуплицирует по ключу).
 */
function useAuthState() {
  const queryClient = useQueryClient()
  const { getInitDataRaw, user: telegramUser } = useTelegram()
  const [authAttempted, setAuthAttempted] = useState(false)
  // Идёт релогин по initData поверх кэшированного токена. Пока он в полёте,
  // GET /auth/me не нужен: ответ логина кладёт того же пользователя в кеш.
  // Раньше оба запроса уходили параллельно, и повисший /auth/me, дотикав свои
  // ретраи через полторы минуты, выкидывал на «Нет связи с сервером» человека,
  // который давно вошёл. Отдельный state, а не isPending мутации: тот
  // обновляется через notifyManager отдельным тиком, и между setAuthAttempted
  // и ним был бы рендер, в котором query уже включён.
  const [reloginPending, setReloginPending] = useState(false)
  const authRetries = useRef(0) // кап повторов авто-релогина (стоп бесконечного цикла → белый экран)
  // Синхронный замок попытки логина. authAttempted — state: он применяется
  // только к следующему рендеру, а эффект успевает запуститься повторно
  // (StrictMode в dev, смена ссылки на мутацию в зависимостях) и отправить
  // второй-третий POST /auth/telegram. ref закрывает вход мгновенно.
  const attemptStarted = useRef(false)
  const { isCurator, setIsCurator, setIsAlsoClient, setIsAlsoCurator, viewingClientId } = useCurator()

  // Query for current user
  const {
    data: user,
    isLoading: isUserLoading,
    error: userError,
    refetch: refetchUser,
  } = useQuery({
    queryKey: ['user', 'me'],
    queryFn: async () => {
      const data: any = await api.getMe()
      // Reconcile role from /auth/me (source of truth) — corrects a stale
      // tfa_is_curator flag when the session is loaded from a cached token.
      // Skipped under admin impersonation, which manages role state itself.
      if (localStorage.getItem('tfa_admin_view') !== '1') {
        setIsCurator(!!data?.isCurator)
      }
      // Sync dual-role flags from /auth/me response
      if (data?.isCurator && data?.isAlsoClient != null) {
        setIsAlsoClient(!!data.isAlsoClient)
      }
      if (!data?.isCurator && data?.isAlsoCurator != null) {
        setIsAlsoCurator(!!data.isAlsoCurator)
      }
      return data
    },
    // Только после решения об автологине: если он релогинит по initData —
    // /auth/me не ходит вовсе (см. reloginPending), иначе идёт как раньше.
    enabled: !!api.getToken() && authAttempted && !reloginPending,
    retry: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })

  // Telegram login mutation
  const telegramLoginMutation = useMutation({
    mutationFn: (initData: string) => api.loginWithTelegram(initData),
    onSuccess: (data) => {
      authRetries.current = 0 // успешный логин — сбрасываем кап повторов
      // The login response is the source of truth for role. Set isCurator
      // authoritatively (not just to true) so a stale tfa_is_curator flag —
      // e.g. cached from a previous dev mock-curator session — is cleared on a
      // client login. Otherwise a real client opens in curator mode.
      setIsCurator(!!data.isCurator)
      if (data.isCurator) {
        setIsAlsoClient(!!data.isAlsoClient)
      } else {
        setIsAlsoCurator(!!data.isAlsoCurator)
      }
      queryClient.setQueryData(['user', 'me'], data.user)
    },
  })

  // Email login mutation (fallback)
  const emailLoginMutation = useMutation({
    mutationFn: (email: string) => api.loginWithEmail(email),
    onSuccess: (data) => {
      queryClient.setQueryData(['user', 'me'], data.user)
    },
  })

  // Re-attempt Telegram login when token was cleared (e.g. stale token → 401 from /auth/me).
  // Капаем число повторов: иначе при стабильно падающем логине (например клиент,
  // у которого /auth/me 401-ит снова и снова) образуется бесконечный цикл
  // re-auth → isLoading никогда не сходится → ready() не вызывается → у клиента
  // вечный белый экран (плейсхолдер Telegram). После MAX повторов прекращаем —
  // isLoading сходится и показывается восстановимый ErrorScreen.
  useEffect(() => {
    if (userError && !api.getToken() && authAttempted) {
      if (authRetries.current >= 3) return
      authRetries.current += 1
      attemptStarted.current = false // снимаем замок — это осознанный повтор
      setAuthAttempted(false)
      queryClient.removeQueries({ queryKey: ['user', 'me'] })
    }
  }, [userError, authAttempted, queryClient])

  // Auto-login with Telegram on mount
  useEffect(() => {
    const attemptTelegramLogin = async () => {
      if (authAttempted || attemptStarted.current) return
      attemptStarted.current = true
      if (api.getToken()) {
        // A cached token must not outrank the current Telegram user. Inside a
        // real Telegram client with fresh initData, re-authenticate so the
        // session always matches the current user and the access gate runs —
        // this self-heals a token cached for a different identity (e.g. a stray
        // dev mock-curator login). Exception: admin read-only impersonation
        // deliberately runs on a pre-minted token, so leave it alone.
        const impersonating = localStorage.getItem('tfa_admin_view') === '1'
        // Dual-пользователь осознанно в режиме КЛИЕНТА (переключился из куратора):
        // его client-токен валиден, а релогин по initData в Telegram всегда
        // резолвится в КУРАТОРА (основная личность человека) и перетирает
        // переключение → отскок обратно в куратора. Пропускаем initData-релогин
        // в этом случае. Флаг ставит switchToClientMode (Telegram-only баг).
        const inDualClientMode = localStorage.getItem('tfa_is_also_curator') === 'true'
        const freshInitData = isRealTelegram() ? getInitDataRaw() : ''
        if (freshInitData && !impersonating && !inDualClientMode) {
          setReloginPending(true)
          setAuthAttempted(true)
          try {
            await telegramLoginMutation.mutateAsync(freshInitData)
          } catch (error) {
            if (isAccessClosed(error)) { api.setToken(null); closeMiniApp(); return }
            // Релогин не прошёл (сеть, 5xx) — снятый флаг включит /auth/me с
            // кэшированным токеном: он ещё может быть валиден.
            console.error('Telegram re-auth failed:', error)
          } finally {
            setReloginPending(false)
          }
          return
        }
        setAuthAttempted(true)
        return
      }

      const initDataRaw = getInitDataRaw()
      if (initDataRaw) {
        setAuthAttempted(true)
        try {
          await telegramLoginMutation.mutateAsync(initDataRaw)
        } catch (error) {
          if (isAccessClosed(error)) { closeMiniApp(); return }
          console.error('Telegram login failed:', error)
        }
      } else if (!isRealTelegram() && (import.meta.env.DEV || window.location.hostname.includes('-dev.'))) {
        // Browser dev mode or dev stand (NOT a real Telegram client): auto-login as test user.
        // Override id/username via ?dev_tg=<id>&dev_user=<username>&dev_name=<name>
        // or persisted localStorage `dev_tg` / `dev_user` / `dev_name`.
        setAuthAttempted(true)
        const urlParams = new URLSearchParams(window.location.search)
        const tgIdParam = urlParams.get('dev_tg') || localStorage.getItem('dev_tg')
        if (urlParams.get('dev_tg')) localStorage.setItem('dev_tg', urlParams.get('dev_tg')!)
        const userParam = urlParams.get('dev_user') || localStorage.getItem('dev_user') || 'NBL_Dev'
        if (urlParams.get('dev_user')) localStorage.setItem('dev_user', urlParams.get('dev_user')!)
        const nameParam = urlParams.get('dev_name') || localStorage.getItem('dev_name') || 'NobodyLabs'
        if (urlParams.get('dev_name')) localStorage.setItem('dev_name', urlParams.get('dev_name')!)
        const avatarParam = urlParams.get('dev_avatar') || localStorage.getItem('dev_avatar') || undefined
        if (urlParams.get('dev_avatar')) localStorage.setItem('dev_avatar', urlParams.get('dev_avatar')!)
        const tgId = tgIdParam ? Number(tgIdParam) : 7979869748
        const mockUser: Record<string, unknown> = { id: tgId, first_name: nameParam, username: userParam }
        if (avatarParam) mockUser.photo_url = avatarParam
        const mockInitData = `user=${encodeURIComponent(JSON.stringify(mockUser))}`
        try {
          await telegramLoginMutation.mutateAsync(mockInitData)
        } catch (error) {
          if (isAccessClosed(error)) { closeMiniApp(); return }
          console.error('Dev auto-login failed:', error)
        }
      } else {
        setAuthAttempted(true)
      }
    }

    // Флаг проставляем и по завершении попытки: setAuthAttempted внутри
    // выполняется до await, а StrictMode (dev) откатывает состояние первого
    // прохода эффекта — с синхронным замком повтора уже не будет, и isLoading
    // без этого finally завис бы на экране загрузки.
    void attemptTelegramLogin().finally(() => setAuthAttempted(true))
  }, [getInitDataRaw, authAttempted, telegramLoginMutation])

  // Register a re-auth provider for the API layer: when any request gets a 401
  // (expired JWT mid-session, e.g. saving a workout set after the app sat open),
  // the axios interceptor calls this to silently re-login via Telegram initData
  // and replays the failed request — no «Invalid or expired token» toast.
  useEffect(() => {
    api.setReauthHandler(async () => {
      try {
        // Дуальный пользователь в режиме клиента: initData без forceMode
        // резолвится в куратора, и продлённая сессия молча сменила бы роль.
        // Касается обеих веток ниже — dev-мок на стенде должен вести себя как прод.
        const forceMode = localStorage.getItem('tfa_is_also_curator') === 'true' ? 'client' : undefined
        const raw = getInitDataRaw()
        if (raw) {
          await api.loginWithTelegram(raw, forceMode)
          return true
        }
        // Dev/preview fallback — rebuild the mock initData from persisted dev_* keys.
        // Не в настоящем Telegram: там пустой initData — сбой, а не повод
        // входить тестовой персоной (см. isRealTelegram).
        if (!isRealTelegram() && (import.meta.env.DEV || window.location.hostname.includes('-dev.'))) {
          const tgId = Number(localStorage.getItem('dev_tg')) || 7979869748
          const userName = localStorage.getItem('dev_user') || 'NBL_Dev'
          const name = localStorage.getItem('dev_name') || 'NobodyLabs'
          const avatar = localStorage.getItem('dev_avatar') || undefined
          const mockUser: Record<string, unknown> = { id: tgId, first_name: name, username: userName }
          if (avatar) mockUser.photo_url = avatar
          await api.loginWithTelegram(`user=${encodeURIComponent(JSON.stringify(mockUser))}`, forceMode)
          return true
        }
        return false
      } catch {
        return false
      }
    })
    return () => api.setReauthHandler(null)
  }, [getInitDataRaw])

  // Logout
  const logout = useCallback(() => {
    api.setToken(null)
    queryClient.clear()
    attemptStarted.current = false // после выхода вход должен снова стать возможен
    setAuthAttempted(false)
  }, [queryClient])

  // Login with email (fallback for testing)
  const loginWithEmail = useCallback(
    async (email: string) => {
      return emailLoginMutation.mutateAsync(email)
    },
    [emailLoginMutation]
  )

  // Computed state
  const isAuthenticated = !!user
  // Пользователь уже есть — грузить нечего, что бы ни говорили флаги в полёте.
  // Без этой отсечки залипший isPending мутации (наблюдается при повторном
  // монтировании дерева) держал бы экран загрузки поверх готовых данных.
  // !authAttempted теперь и при наличии токена: query ['user','me'] включается
  // только после решения об автологине, и до него isUserLoading = false —
  // без этого был бы кадр «не грузимся, не вошли» с ложным экраном ошибки.
  const isLoading =
    !isAuthenticated &&
    (isUserLoading ||
      telegramLoginMutation.isPending ||
      emailLoginMutation.isPending ||
      reloginPending ||
      !authAttempted)

  const error = userError || telegramLoginMutation.error || emailLoginMutation.error

  // Check if onboarding is needed (curators skip onboarding).
  // Per spec, all three products require `onboardingCompleted=true` (which is
  // reset server-side on every product switch) plus the appropriate fill:
  //   CLUB       → Анкета 1
  //   TFA_STEP1  → both Анкета 1 и Анкета 2
  //   TFA_STEP2  → both Анкета 1 и Анкета 2
  const l1 = (user as any)?.questionnaireLevel1
  const l2 = (user as any)?.questionnaireLevel2
  const l1Filled = !!l1 && typeof l1 === 'object' && Object.keys(l1).length > 0
  const l2Filled = !!l2 && typeof l2 === 'object' && Object.keys(l2).length > 0
  const product = (user as any)?.product
  // Три тарифа: Программе и Тренингу анкета не нужна — онбординг закрывает сервер.
  const requiredFill = product === 'PROGRAM' || product === 'TRAINING' ? true : product === 'CLUB' ? l1Filled : (l1Filled && l2Filled)
  const onboardingDone = !!user?.onboardingCompleted && requiredFill
  const needsOnboarding = !!user && !onboardingDone && !isCurator && !viewingClientId

  return {
    user,
    telegramUser,
    isAuthenticated,
    isLoading,
    error: error as Error | null,
    needsOnboarding,
    isCurator,
    login: loginWithEmail,
    logout,
    refetchUser,
  }
}

export type AuthContext = ReturnType<typeof useAuthState>

const AuthCtx = createContext<AuthContext | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const value = useAuthState()
  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>
}

/**
 * Доступ к общей сессии. Все потребители читают одно состояние — авто-логин
 * происходит ровно один раз за загрузку приложения.
 */
export function useAuth(): AuthContext {
  const ctx = useContext(AuthCtx)
  if (!ctx) throw new Error('useAuth вызван вне <AuthProvider> — оберните дерево в App.tsx')
  return ctx
}
